import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Eye, Monitor, Smartphone, Tablet, RefreshCw } from "lucide-react";
import { fmtT } from "@/components/videos/VideoReviewer";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "");
const ICON = { Mobile: Smartphone, Tablet, Desktop: Monitor };

function Watched({ row, durations }) {
  const n = String(row.last_version || "");
  const t = (row.progress || {})[n] || 0;
  const d = durations[n] || 0;
  const pct = d ? Math.min(100, Math.round((t / d) * 100)) : null;
  return (
    <div className="min-w-[150px]" data-testid={`video-viewer-watched-${row.id}`}>
      <p className="text-xs text-[#06060C] font-mono-data">{fmtT(t)}{d ? ` of ${fmtT(d)}` : ""} <span className="text-[#9FA1A4]">· v{n}</span></p>
      {pct !== null && <div className="h-1.5 mt-1 rounded-full bg-[#F0F0F5] overflow-hidden"><div className={`h-full rounded-full ${pct >= 95 ? "bg-[#1f9d55]" : "bg-[#723FFF]"}`} style={{ width: `${pct}%` }} /></div>}
    </div>
  );
}

export function VideoViewers({ videoId }) {
  const [data, setData] = useState(null);
  const load = () => api.get(`/console/videos/${videoId}/views`).then(({ data: d }) => setData(d)).catch(() => setData({ views: [], durations: {} }));
  useEffect(() => { load(); }, [videoId]); // eslint-disable-line
  const rows = data?.views || [];
  return (
    <div className="mt-8 rounded-2xl border border-[#EEEFEF] bg-white" data-testid="video-viewers">
      <div className="px-4 py-3 border-b border-[#f0f0f3] flex items-center gap-2">
        <Eye className="h-3.5 w-3.5 text-[#9FA1A4]" />
        <p className="text-xs font-mono-data uppercase tracking-wider text-[#9FA1A4]" data-testid="video-viewers-count">Review link viewers · {rows.length}</p>
        <button data-testid="video-viewers-refresh" onClick={load} title="Refresh" className="ml-auto text-[#9FA1A4] hover:text-[#723FFF]"><RefreshCw className="h-3.5 w-3.5" /></button>
      </div>
      {data && rows.length === 0 && <p className="p-4 text-sm text-[#9FA1A4]" data-testid="video-viewers-empty">Nobody has opened the review link yet.</p>}
      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] font-mono-data uppercase tracking-wider text-[#9FA1A4]">
              <th className="px-4 py-2 font-normal">Viewer</th><th className="px-4 py-2 font-normal">Views</th><th className="px-4 py-2 font-normal">First opened</th>
              <th className="px-4 py-2 font-normal">Last opened</th><th className="px-4 py-2 font-normal">Watched</th><th className="px-4 py-2 font-normal">Device</th>
            </tr></thead>
            <tbody className="divide-y divide-[#f6f6f8]">
              {rows.map((r) => { const I = ICON[r.kind] || Monitor; return (
                <tr key={r.id} data-testid={`video-viewer-${r.id}`}>
                  <td className="px-4 py-2.5"><p className="font-medium text-[#06060C]">{r.name}</p><p className="text-xs text-[#9FA1A4]">{r.email}</p></td>
                  <td className="px-4 py-2.5 font-mono-data" data-testid={`video-viewer-views-${r.id}`}>{r.views}</td>
                  <td className="px-4 py-2.5 text-xs text-[#595D62]">{fmt(r.first_viewed_at)}</td>
                  <td className="px-4 py-2.5 text-xs text-[#595D62]">{fmt(r.last_viewed_at)}</td>
                  <td className="px-4 py-2.5"><Watched row={r} durations={data.durations || {}} /></td>
                  <td className="px-4 py-2.5 text-xs text-[#595D62]"><span className="flex items-center gap-1.5"><I className="h-3.5 w-3.5" /> {r.browser} · {r.os}</span></td>
                </tr>); })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
